import axios, {
  AxiosInstance,
  AxiosInterceptorManager,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { JWT_KEY } from "../../config/constant";
import { renewAccessToken } from "../auth";
import { clearAuthStorage } from "../../utils/authStorage";
import {
  createTokenRefreshFailedError,
  isNotActiveUser,
  isDeletedUser,
} from "../../utils/errorCodes";

export interface BaseResponse<T = any> {
  code: number;
  isSuccess: boolean;
  message: string;
  /**
   * i18n W2 (2026-05-15) — BE BaseResponseStatus enum.name() (예: "NOT_ACTIVE_USER").
   * 실패 응답에만 포함. 성공이거나 BE 구버전 응답이면 undefined.
   * FE 분기는 errorName 우선, 한글 message 비교는 하위호환 fallback.
   */
  errorName?: string;
  result: T;
}

interface CustomInstance extends AxiosInstance {
  interceptors: {
    request: AxiosInterceptorManager<AxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse<BaseResponse>>;
  };
  getUri(config?: AxiosRequestConfig): string;
  request<T>(config: AxiosRequestConfig): Promise<T>;
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  head<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  options<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
}

// ──────────────────────────────────────────────────────────────
// 2026-04-19 fix(#4): axios 요청 타임아웃 도입
// (기존) timeout 미설정 → default 0 = 무한 대기.
//   WebView/네트워크가 TLS/DNS 단계에서 stuck 상태면 응답도 에러도
//   없이 영원히 pending → DefaultLayout 의 `setIsLoading(false)` 가
//   호출되지 않아 "뉴런 연결중 🧠" 로티가 무한 재생되는 증상 유발.
// (수정) 15초 타임아웃 부여 → 실패 시 axios 가 `ECONNABORTED` 로
//   throw → ErrorBoundary 가 catch → 유저는 에러 화면 + 재시도 가능.
// 15초는 일반 API 응답(수백 ms~수 초) 대비 넉넉한 여유, 동시에 유저
// 체감 한계선(약 20초) 이하로 설정.
// ──────────────────────────────────────────────────────────────
const API_TIMEOUT_MS = 15_000;

const request: CustomInstance = axios.create({
  baseURL: process.env.REACT_APP_API,
  timeout: API_TIMEOUT_MS,
  headers: {
    accept: "application/json",
  },
});

// X-Platform 트래킹 — 브카는 native shell + WebView 구조라 앱 안에서도 이 axios 가 호출됨.
// 단순히 "web" 으로 고정하면 iOS/Android 앱 유저가 모두 web 으로 분류됨.
// 네이티브 셸이 주입한 인터페이스 (Android: window.Android, iOS: window.webkit.messageHandlers)
// 존재 여부로 진짜 플랫폼 식별. 둘 다 없으면 진짜 브라우저(web).
function resolveClientPlatform(): "ios" | "aos" | "web" {
  const w = window as any;
  if (typeof w.Android !== "undefined") return "aos";
  if (w.webkit?.messageHandlers?.getToken) return "ios";
  return "web";
}

request.interceptors.request.use(
  (config) => {
    if (config.headers) {
      config.headers["X-Platform"] = resolveClientPlatform();
    }

    const jwt = window.localStorage.getItem(JWT_KEY) ?? "";

    if (!jwt) return config;

    if (config.headers && !config.headers.Authorization)
      config.headers.Authorization = `Bearer ${jwt}`;

    return config;
  },
  (error) => Promise.reject(error)
);

// ──────────────────────────────────────────────────────────────
// 2026-04-19 fix(#3): 토큰 재발급 하드닝
// (기존) 외주사가 2024-05 PR #451로 renewPromise 단일화는 했지만,
//   1) renewAccessToken()이 throw하면 모든 awaiter에 전파 → 다중 logout
//   2) 재발급 성공 후에도 401 반복되면 무한 루프 발생 가능
// (수정)
//   1) .catch(() => null)로 throw를 정규화
//   2) 재시도 플래그로 한 요청당 최대 1회만 재발급 → 인증 확정 실패 처리
// ──────────────────────────────────────────────────────────────
let renewPromise: Promise<string | null> | null = null;
const clearRenewPromise = () => (renewPromise = null);

// 재발급 후에도 동일 요청이 또 401이면 Loop로 간주하고 확정 실패 처리
interface InternalRetryConfig extends AxiosRequestConfig {
  _bcRetriedAfterRenew?: boolean;
}

request.interceptors.response.use(
  async ({ config, data }) => {
    if (data.code === 401) {
      // 재발급 후 재시도 경험이 있으면 무한 루프 차단
      if ((config as InternalRetryConfig)._bcRetriedAfterRenew) {
        throw createTokenRefreshFailedError();
      }

      // renew에 대한 Promise 객체가 없을 경우
      if (!renewPromise) {
        // 신규 토큰요청을 보내고, 끝나면 clear
        // .catch로 throw를 null 리턴으로 정규화 → awaiter 다수에 예외 전파 방지
        renewPromise = renewAccessToken()
          .catch((e) => {
            console.log("renewAccessToken error:", e);
            return null;
          })
          .finally(clearRenewPromise);
      }

      // Promise 끝날 때까지 대기
      const accessToken = await renewPromise;

      // 토큰이 정상적으로 넘어왔고, 이전 요청이 토큰이 있었다면
      if (accessToken && config.headers?.Authorization) {
        // 헤더 토큰 새로 넣기
        config.headers.Authorization = `Bearer ${accessToken}`;

        // 재시도 플래그 설정 후 다시 요청 보내기
        (config as InternalRetryConfig)._bcRetriedAfterRenew = true;
        return request(config);
      } else {
        throw createTokenRefreshFailedError();
      }
    } else if (isDeletedUser(data) || isNotActiveUser(data)) {
      console.log(data.message);
      clearAuthStorage();
      window.location.href = `${process.env.REACT_APP_URL}/login`;
      return data;
    }

    return data;
  },
  (error) => Promise.reject(error)
);

export default request;
